import type { ParcelasModel } from 'src/app/models/form-common/parcelas.model';
import type {
    ParcelasDropdownDataSourceFactory,
    SourceAwareParcelasCatalogRow
} from './parcelas-dropdown-datasource.factory';
import { ParcelasCatalogSerializerAdapter } from './parcelas-catalog-serializer.adapter';
import type { ParcelasMapCatalogContext } from './parcelas-map.adapter';

export type ParcelasFormControlLike = {
    readonly value: unknown;
};

export type ParcelasFormGroupLike = {
    readonly get: (fieldName: string) => ParcelasFormControlLike | null;
};

export type ParcelasFormBuilderRefLike = {
    readonly model?: ParcelasFormGroupLike | null;
    readonly resolveDropdownRawValue: <TEntity = unknown>(fieldName: string, value?: unknown) => TEntity | null;
};

export type ParcelasDropdownContextFactoryContext = {
    readonly dataSourceFactory: ParcelasDropdownDataSourceFactory;
    readonly formBuilderRef: ParcelasFormBuilderRefLike | null | undefined;
    readonly formModel: Partial<ParcelasModel> | null | undefined;
    readonly fallbackModel: Partial<ParcelasModel> | null | undefined;
    readonly legacyCultivoSelected: unknown;
    readonly legacyVariedadSelected: unknown;
};

/**
 * Builds reusable catalog/dropdown context for Parcelas adapters.
 *
 * This keeps current-control resolution, OptionStore raw lookup and legacy selected fallbacks out of the Angular
 * component while preserving the current compatibility path during the Reactive Form Builder migration.
 */
export class ParcelasDropdownContextFactory {

    public constructor(private readonly context: ParcelasDropdownContextFactoryContext) {}

    public createMapCatalogContext(): ParcelasMapCatalogContext {
        const currentCultivoValue = this.resolveCurrentFieldValue('id_cultivo');
        const currentVariedadValue = this.resolveCurrentFieldValue('id_variedad');

        return {
            dataSourceFactory: this.context.dataSourceFactory,
            formModel: this.context.formModel,
            fallbackModel: this.context.fallbackModel,
            currentCultivoValue,
            currentVariedadValue,
            selectedCultivo: this.resolveCurrentDropdownRawValue<SourceAwareParcelasCatalogRow>(
                'id_cultivo',
                currentCultivoValue,
                this.context.legacyCultivoSelected,
            ),
            selectedVariedad: this.resolveCurrentDropdownRawValue<SourceAwareParcelasCatalogRow>(
                'id_variedad',
                currentVariedadValue,
                this.context.legacyVariedadSelected,
            ),
            legacyCultivoPayloadValue: this.toLegacyCultivoPayloadValue(currentCultivoValue),
            legacyVariedadPayloadValue: this.toLegacyVariedadPayloadValue(currentVariedadValue),
        };
    }

    public resolveCurrentFieldValue(fieldName: string): unknown {
        const controlValue = this.context.formBuilderRef?.model?.get(fieldName)?.value;

        if (controlValue !== null && controlValue !== undefined) {
            return controlValue;
        }

        const formValue = this.context.dataSourceFactory.readRecordValue(this.context.formModel, fieldName);

        if (formValue !== null && formValue !== undefined) {
            return formValue;
        }

        return this.context.dataSourceFactory.readRecordValue(this.context.fallbackModel, fieldName);
    }

    public resolveCurrentDropdownRawValue<TEntity = unknown>(
        fieldName: string,
        currentValue: unknown,
        legacySelected: unknown,
    ): TEntity | null {
        const rawFromCurrentValue = this.context.formBuilderRef?.resolveDropdownRawValue<TEntity>(
            fieldName,
            currentValue,
        ) ?? null;

        if (rawFromCurrentValue !== null) {
            return rawFromCurrentValue;
        }

        return legacySelected !== null && typeof legacySelected === 'object' ? legacySelected as TEntity : null;
    }

    public toLegacyCultivoPayloadValue(value: unknown): unknown {
        return ParcelasCatalogSerializerAdapter.toLegacyCultivoPayloadValue({
            dataSourceFactory: this.context.dataSourceFactory,
            value,
            selectedRow: this.context.formBuilderRef
                ?.resolveDropdownRawValue<SourceAwareParcelasCatalogRow>('id_cultivo', value) ?? null,
        });
    }

    public toLegacyVariedadPayloadValue(value: unknown): unknown {
        return ParcelasCatalogSerializerAdapter.toLegacyVariedadPayloadValue({
            dataSourceFactory: this.context.dataSourceFactory,
            value,
            selectedRow: this.context.formBuilderRef
                ?.resolveDropdownRawValue<SourceAwareParcelasCatalogRow>('id_variedad', value) ?? null,
        });
    }
}
